import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Form, Table, Button, Alert, Badge, Spinner as BSSpinner } from 'react-bootstrap';
import { FaPlus, FaTrash, FaSync } from 'react-icons/fa';
import * as api from '../services/api';
import './DeployAgent.css';

const DeployAgent = () => {
  // State management
  const [parentAgents, setParentAgents] = useState([]);
  const [selectedParentAgent, setSelectedParentAgent] = useState(null);
  const [microAgents, setMicroAgents] = useState([]);
  const [miniAgents, setMiniAgents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingAgents, setLoadingAgents] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [actionInProgress, setActionInProgress] = useState({});

  // Load parent agents on mount
  useEffect(() => {
    loadParentAgents();
  }, []);

  // Auto-dismiss success messages after 5 seconds
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  const loadParentAgents = async () => {
    try {
      setLoadingAgents(true);
      setError(null);
      
      console.log('Loading parent agents from API...');
      
      // Use the proper API helper function like AgentsList.js does
      const response = await api.getAgents();
      
      console.log('Parent agents response:', response);
      
      if (response && response.agents && Array.isArray(response.agents)) {
        // Transform to the format needed for DeployAgent
        const transformedAgents = response.agents.map(agent => ({
          agent_id: parseInt(agent.uniqueIdentifier) || parseInt(agent.id),
          agent_name: agent.agentName || agent.name,
          description: agent.agentDescription || agent.description,
          github_account: agent.repoAccount,
          github_repo: agent.repoName,
          aws_region: agent.awsRegion,
          aws_account: agent.awsAccountNumber,
          status: agent.commissioned ? 'production' : 'development',
          // Infrastructure deployment fields
          core_deployed_development: agent.deployedInDev,
          core_deployed_production: agent.deployedInProd,
          accessories_deployed_development: agent.accessories_deployed_development,
          accessories_deployed_production: agent.accessories_deployed_production,
          api_deployed_development: agent.api_deployed_development,
          api_deployed_production: agent.api_deployed_production
        }));
        
        console.log(`Loaded ${transformedAgents.length} parent agents`);
        setParentAgents(transformedAgents);

        // Set default to AgentDeveloper if it exists
        const agentDeveloper = transformedAgents.find(agent => 
          agent.agent_name === 'AgentDeveloper'
        );
        if (agentDeveloper) {
          setSelectedParentAgent(agentDeveloper);
          await loadMicroAgents(agentDeveloper.agent_name);
        }
      } else {
        console.error('Unexpected response format for parent agents:', response);
        setError('Failed to load parent agents. Invalid response format.');
      }
    } catch (err) {
      console.error('Error loading parent agents:', err);
      setError('Failed to load parent agents. Please try again.');
    } finally {
      setLoadingAgents(false);
    }
  };

  // Load micro-agents when parent agent is selected
  const handleParentAgentChange = async (e) => {
    const agentId = e.target.value;
    
    // Clear selection
    if (!agentId) {
      setSelectedParentAgent(null);
      setMicroAgents([]);
      setMiniAgents([]);
      return;
    }

    try {
      // Find the selected agent
      const agent = parentAgents.find(a => a.agent_id === parseInt(agentId));
      
      if (!agent) {
        console.error('Selected agent not found:', agentId);
        setError('Selected agent not found');
        return;
      }

      // Update selected agent
      setSelectedParentAgent(agent);
      
      // Clear existing micro/mini agents immediately to prevent stale data
      setMicroAgents([]);
      setMiniAgents([]);
      
      // Load new data - pass the agent name, not the ID
      await loadMicroAgents(agent.agent_name);
    } catch (err) {
      console.error('Error in handleParentAgentChange:', err);
      setError('Failed to load agent data. Please try again.');
      // Reset state on error
      setSelectedParentAgent(null);
      setMicroAgents([]);
      setMiniAgents([]);
    }
  };

  const loadMicroAgents = async (parentAgentName) => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('Loading micro-agents for parent agent:', parentAgentName);
      
      // FIXED: Use the proper API helper function like AgentsList.js pattern
      // This calls POST /db/micro-agents (NOT /db/mini-agent-tree)
      const response = await api.getMicroAgents(parentAgentName);

      console.log('Micro-agents response:', response);

      // Handle response - getMicroAgents returns { success: true, microAgents: [...] }
      let allResources = [];
      
      if (response && response.success && Array.isArray(response.microAgents)) {
        // Transform from the API format (camelCase) to the format expected by DeployAgent (snake_case)
        allResources = response.microAgents.map(ma => ({
          micro_agent_id: ma.uniqueIdentifier,
          micro_agent_number: ma.agentNumber,
          micro_agent_name: ma.microAgentName,
          micro_agent_description: ma.microAgentDescription,
          micro_agent_deployed_dev: ma.deployedInDev,
          micro_agent_deployed_prod: ma.deployedInProd,
          micro_agent_status: ma.commissioned ? 'production' : 'development',
          is_mini_agent: ma.isMiniAgent,
          parent_agent_name: ma.parentAgentName
        }));
      } else if (response && Array.isArray(response.microAgents)) {
        // Alternative format without success flag
        allResources = response.microAgents.map(ma => ({
          micro_agent_id: ma.uniqueIdentifier,
          micro_agent_number: ma.agentNumber,
          micro_agent_name: ma.microAgentName,
          micro_agent_description: ma.microAgentDescription,
          micro_agent_deployed_dev: ma.deployedInDev,
          micro_agent_deployed_prod: ma.deployedInProd,
          micro_agent_status: ma.commissioned ? 'production' : 'development',
          is_mini_agent: ma.isMiniAgent,
          parent_agent_name: ma.parentAgentName
        }));
      } else {
        console.warn('Unexpected response format for micro-agents:', response);
        allResources = [];
      }

      // Ensure we have an array
      if (!Array.isArray(allResources)) {
        console.error('allResources is not an array:', allResources);
        allResources = [];
      }
      
      // Separate micro-agents and mini-agents based on is_mini_agent flag
      const micros = allResources.filter(ma => ma && !ma.is_mini_agent);
      const minis = allResources.filter(ma => ma && ma.is_mini_agent);
      
      console.log(`Loaded ${micros.length} micro-agents and ${minis.length} mini-agents`);
      
      setMicroAgents(micros);
      setMiniAgents(minis);
    } catch (err) {
      console.error('Error loading micro-agents:', err);
      setError(`Failed to load micro-agents: ${err.message || 'Unknown error'}`);
      setMicroAgents([]);
      setMiniAgents([]);
    } finally {
      setLoading(false);
    }
  };

  // Deploy or re-deploy a micro-agent
  const handleDeploy = async (microAgent, environment) => {
    if (!microAgent || !selectedParentAgent) {
      setError('Invalid agent or parent agent not selected');
      return;
    }

    const actionKey = `${microAgent.micro_agent_id}-${environment}`;
    
    try {
      setActionInProgress(prev => ({ ...prev, [actionKey]: true }));
      setError(null);
      setSuccess(null);

      const payload = {
        agentNumber: "130",
        payload: {
          parentAgentName: selectedParentAgent.agent_name || '',
          parentAgentRepo: `${selectedParentAgent.github_account || ''}/${selectedParentAgent.github_repo || ''}`,
          microAgentNumber: microAgent.micro_agent_number,
          environment: environment === 'dev' ? 'dev' : 'prod'
        },
        environment: "production"
      };

      const response = await api.makeAuthenticatedRequest('POST', '/agents/130', payload);

      console.log('Deploy agent response:', response);

      // Check if response indicates an error (4xx, 5xx status codes or has error field)
      const hasError = response?.error || 
                      (response?.statusCode && response.statusCode >= 400);
      
      // Success if statusCode is 200 or 202 (202 = Accepted, operation started)
      // and no error field is present
      if (response && !hasError && (response.success || response.statusCode === 200 || response.statusCode === 202)) {
        // Use the message from the API response (body should be parsed by api.js)
        let successMessage = response.message || 
          `Successfully initiated deployment of ${microAgent.micro_agent_name || 'agent'} to ${environment === 'dev' ? 'development' : 'production'}`;
        
        // If executionArn is present, add it as informational context
        if (response.executionArn) {
          successMessage += ` (Execution ARN: ${response.executionArn})`;
        }
        
        setSuccess(successMessage);
        
        // Reload micro-agents to get updated deployment status
        setTimeout(() => {
          if (selectedParentAgent && selectedParentAgent.agent_name) {
            loadMicroAgents(selectedParentAgent.agent_name);
          }
        }, 2000);
      } else {
        // Failure case
        const errorMessage = response?.error?.message || response?.message || 'Deployment failed';
        throw new Error(errorMessage);
      }
    } catch (err) {
      console.error('Error deploying micro-agent:', err);
      setError(`Failed to deploy ${microAgent.micro_agent_name || 'agent'}: ${err.message || 'Unknown error'}`);
    } finally {
      setActionInProgress(prev => ({ ...prev, [actionKey]: false }));
    }
  };

  // Deploy infrastructure component (Core Infrastructure, Accessories, Api Stack)
  const handleInfrastructureDeploy = async (componentName, agentNumber, environment) => {
    if (!selectedParentAgent) {
      setError('Parent agent not selected');
      return;
    }

    const actionKey = `${componentName}-${environment}`;
    
    try {
      setActionInProgress(prev => ({ ...prev, [actionKey]: true }));
      setError(null);
      setSuccess(null);

      const payload = {
        agentNumber: agentNumber,
        payload: {
          parentAgentName: selectedParentAgent.agent_name || '',
          parentAgentRepo: `${selectedParentAgent.github_account || ''}/${selectedParentAgent.github_repo || ''}`,
          environment: environment === 'dev' ? 'dev' : 'prod'
        },
        environment: "production"
      };

      const response = await api.makeAuthenticatedRequest('POST', `/agents/${agentNumber}`, payload);

      console.log(`Deploy ${componentName} response:`, response);

      // Check if response indicates an error
      const hasError = response?.error || 
                      (response?.statusCode && response.statusCode >= 400);
      
      // Success if statusCode is 200 or 202
      if (response && !hasError && (response.success || response.statusCode === 200 || response.statusCode === 202)) {
        let successMessage = response.message || 
          `Successfully initiated deployment of ${componentName} to ${environment === 'dev' ? 'development' : 'production'}`;
        
        if (response.executionArn) {
          successMessage += ` (Execution ARN: ${response.executionArn})`;
        }
        
        setSuccess(successMessage);
        
        // Reload parent agent to get updated deployment status
        setTimeout(() => {
          loadParentAgents();
        }, 2000);
      } else {
        const errorMessage = response?.error?.message || response?.message || 'Deployment failed';
        throw new Error(errorMessage);
      }
    } catch (err) {
      console.error(`Error deploying ${componentName}:`, err);
      setError(`Failed to deploy ${componentName}: ${err.message || 'Unknown error'}`);
    } finally {
      setActionInProgress(prev => ({ ...prev, [actionKey]: false }));
    }
  };

  // Delete a micro-agent
  const handleDelete = async (microAgent, environment) => {
    if (!microAgent || !selectedParentAgent) {
      setError('Invalid agent or parent agent not selected');
      return;
    }

    const envName = environment === 'dev' ? 'DEVELOPMENT' : 'PRODUCTION';
    const confirmMessage = `Are you sure you want to delete ${microAgent.micro_agent_name || 'this agent'} from ${envName}? This action cannot be undone.`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    const actionKey = `${microAgent.micro_agent_id}-${environment}-delete`;
    
    try {
      setActionInProgress(prev => ({ ...prev, [actionKey]: true }));
      setError(null);
      setSuccess(null);

      const payload = {
        micro_agent_id: microAgent.micro_agent_id,
        environment: environment === 'dev' ? 'DEV' : 'PROD'
      };

      const response = await api.makeAuthenticatedRequest('POST', '/delete-micro-agent', payload);

      if (response && (response.success || response.statusCode === 202)) {
        setSuccess(`Successfully deleted ${microAgent.micro_agent_name || 'agent'} from ${envName.toLowerCase()}`);
        // Reload micro-agents to get updated deployment status
        setTimeout(() => {
          if (selectedParentAgent && selectedParentAgent.agent_name) {
            loadMicroAgents(selectedParentAgent.agent_name);
          }
        }, 2000);
      } else {
        throw new Error(response?.message || 'Deletion failed');
      }
    } catch (err) {
      console.error('Error deleting micro-agent:', err);
      setError(`Failed to delete ${microAgent.micro_agent_name || 'agent'}: ${err.message || 'Unknown error'}`);
    } finally {
      setActionInProgress(prev => ({ ...prev, [actionKey]: false }));
    }
  };

  // Render action buttons for infrastructure components
  const renderInfrastructureActions = (componentName, agentNumber, isDeployedDev, isDeployedProd, environment) => {
    const isDeployed = environment === 'dev' ? isDeployedDev : isDeployedProd;
    const actionKey = `${componentName}-${environment}`;
    const isDeploying = actionInProgress[actionKey];

    if (isDeployed) {
      return (
        <div className="env-actions deployed">
          <Badge bg="success" className="status-badge">Deployed</Badge>
          <div className="action-buttons">
            <Button 
              variant="primary" 
              size="sm" 
              onClick={() => handleInfrastructureDeploy(componentName, agentNumber, environment)}
              disabled={isDeploying}
              className="action-btn deploy-only"
            >
              {isDeploying ? (
                <BSSpinner animation="border" size="sm" />
              ) : (
                <>
                  <FaSync className="me-1" /> Re-deploy
                </>
              )}
            </Button>
          </div>
        </div>
      );
    } else {
      return (
        <div className="env-actions not-deployed">
          <Badge bg="secondary" className="status-badge">Not Deployed</Badge>
          <div className="action-buttons">
            <Button 
              variant="success" 
              size="sm" 
              onClick={() => handleInfrastructureDeploy(componentName, agentNumber, environment)}
              disabled={isDeploying}
              className="action-btn deploy-only"
            >
              {isDeploying ? (
                <BSSpinner animation="border" size="sm" />
              ) : (
                <>
                  <FaPlus className="me-1" /> Deploy
                </>
              )}
            </Button>
          </div>
        </div>
      );
    }
  };

  // Render action buttons for an environment
  const renderEnvironmentActions = (microAgent, environment) => {
    if (!microAgent) {
      return null;
    }

    const isDeployed = environment === 'dev' 
      ? microAgent.micro_agent_deployed_dev 
      : microAgent.micro_agent_deployed_prod;
    
    const deployKey = `${microAgent.micro_agent_id}-${environment}`;
    const deleteKey = `${microAgent.micro_agent_id}-${environment}-delete`;
    const isDeploying = actionInProgress[deployKey];
    const isDeleting = actionInProgress[deleteKey];

    if (isDeployed) {
      return (
        <div className="env-actions deployed">
          <Badge bg="success" className="status-badge">Deployed</Badge>
          <div className="action-buttons">
            <Button 
              variant="primary" 
              size="sm" 
              onClick={() => handleDeploy(microAgent, environment)}
              disabled={isDeploying || isDeleting}
              className="action-btn"
            >
              {isDeploying ? (
                <BSSpinner animation="border" size="sm" />
              ) : (
                <>
                  <FaSync className="me-1" /> Re-deploy
                </>
              )}
            </Button>
            <Button 
              variant="danger" 
              size="sm" 
              onClick={() => handleDelete(microAgent, environment)}
              disabled={isDeploying || isDeleting}
              className="action-btn"
            >
              {isDeleting ? (
                <BSSpinner animation="border" size="sm" />
              ) : (
                <>
                  <FaTrash className="me-1" /> Delete
                </>
              )}
            </Button>
          </div>
        </div>
      );
    } else {
      return (
        <div className="env-actions not-deployed">
          <Badge bg="secondary" className="status-badge">Not Deployed</Badge>
          <div className="action-buttons">
            <Button 
              variant="success" 
              size="sm" 
              onClick={() => handleDeploy(microAgent, environment)}
              disabled={isDeploying}
              className="action-btn deploy-only"
            >
              {isDeploying ? (
                <BSSpinner animation="border" size="sm" />
              ) : (
                <>
                  <FaPlus className="me-1" /> Deploy
                </>
              )}
            </Button>
          </div>
        </div>
      );
    }
  };

  // Render infrastructure components section
  const renderInfrastructureSection = () => {
    if (!selectedParentAgent) {
      return null;
    }

    const infrastructureComponents = [
      {
        name: 'Core Infrastructure',
        agentNumber: '131',
        deployedDev: selectedParentAgent.core_deployed_development,
        deployedProd: selectedParentAgent.core_deployed_production
      },
      {
        name: 'Accessories',
        agentNumber: '132',
        deployedDev: selectedParentAgent.accessories_deployed_development,
        deployedProd: selectedParentAgent.accessories_deployed_production
      }
    ];

    return (
      <div className="agents-section">
        <h3 className="section-title">Infrastructure Components</h3>
        <Table responsive bordered hover className="agents-table">
          <thead>
            <tr>
              <th className="col-name">Component</th>
              <th className="col-env">Dev</th>
              <th className="col-env">Prod</th>
            </tr>
          </thead>
          <tbody>
            {infrastructureComponents.map((component) => (
              <tr key={component.name}>
                <td className="col-name">
                  <strong>{component.name}</strong>
                </td>
                <td className="col-env">
                  {renderInfrastructureActions(
                    component.name,
                    component.agentNumber,
                    component.deployedDev,
                    component.deployedProd,
                    'dev'
                  )}
                </td>
                <td className="col-env">
                  {renderInfrastructureActions(
                    component.name,
                    component.agentNumber,
                    component.deployedDev,
                    component.deployedProd,
                    'prod'
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    );
  };

  // Render API Stack section
  const renderApiStackSection = () => {
    if (!selectedParentAgent) {
      return null;
    }

    return (
      <div className="agents-section">
        <h3 className="section-title">API Stack</h3>
        <Table responsive bordered hover className="agents-table">
          <thead>
            <tr>
              <th className="col-name">Component</th>
              <th className="col-env">Dev</th>
              <th className="col-env">Prod</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="col-name">
                <strong>Api Stack</strong>
              </td>
              <td className="col-env">
                {renderInfrastructureActions(
                  'Api Stack',
                  '133',
                  selectedParentAgent.api_deployed_development,
                  selectedParentAgent.api_deployed_production,
                  'dev'
                )}
              </td>
              <td className="col-env">
                {renderInfrastructureActions(
                  'Api Stack',
                  '133',
                  selectedParentAgent.api_deployed_development,
                  selectedParentAgent.api_deployed_production,
                  'prod'
                )}
              </td>
            </tr>
          </tbody>
        </Table>
      </div>
    );
  };

  // Render a section of agents (micro or mini)
  const renderAgentsSection = (agents, title) => {
    if (!Array.isArray(agents) || agents.length === 0) {
      return null;
    }

    return (
      <div className="agents-section">
        <h3 className="section-title">{title}</h3>
        <Table responsive bordered hover className="agents-table">
          <thead>
            <tr>
              <th className="col-number">#</th>
              <th className="col-name">Name</th>
              <th className="col-status">Status</th>
              <th className="col-env">Dev</th>
              <th className="col-env">Prod</th>
            </tr>
          </thead>
          <tbody>
            {agents.map((agent) => {
              if (!agent) return null;
              
              const agentId = agent.micro_agent_id || agent.id;
              const agentNumber = agent.micro_agent_number || '';
              const agentName = agent.micro_agent_name || agent.name || 'Unnamed';
              const agentStatus = agent.micro_agent_status || agent.status || 'unknown';

              return (
                <tr key={agentId || Math.random()}>
                  <td className="col-number">
                    <span className="agent-number-badge">{agentNumber}</span>
                  </td>
                  <td className="col-name">
                    <strong>{agentName}</strong>
                  </td>
                  <td className="col-status">
                    <Badge bg="secondary" className="text-capitalize">
                      {agentStatus}
                    </Badge>
                  </td>
                  <td className="col-env">
                    {renderEnvironmentActions(agent, 'dev')}
                  </td>
                  <td className="col-env">
                    {renderEnvironmentActions(agent, 'prod')}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </div>
    );
  };

  return (
    <Container className="deploy-agent-page py-4">
      <div className="page-header mb-4">
        <h1 className="page-title">Deployment Engine</h1>
        <p className="text-muted">Select a parent agent to view and manage its micro-agents and mini-agents</p>
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)} className="mb-3">
          {error}
        </Alert>
      )}

      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess(null)} className="mb-3">
          {success}
        </Alert>
      )}

      <Row className="mb-4">
        <Col md={6}>
          <Form.Group>
            <Form.Label><strong>Parent Agent</strong></Form.Label>
            <Form.Select 
              value={selectedParentAgent?.agent_id || ''} 
              onChange={handleParentAgentChange}
              disabled={loadingAgents}
              className="parent-agent-select"
            >
              <option value="">Select a parent agent...</option>
              {Array.isArray(parentAgents) && parentAgents.map((agent) => {
                if (!agent) return null;
                const agentId = agent.agent_id;
                const agentName = agent.agent_name || agent.name || `Agent ${agentId}`;
                return (
                  <option key={agentId} value={agentId}>
                    {agentName}
                  </option>
                );
              })}
            </Form.Select>
          </Form.Group>
        </Col>
      </Row>

      {loading && (
        <div className="text-center py-5">
          <BSSpinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </BSSpinner>
          <p className="mt-3 text-muted">Loading resources...</p>
        </div>
      )}

      {!loading && !selectedParentAgent && (
        <div className="empty-state text-center py-5">
          <p className="text-muted">Select a parent agent to view its resources</p>
        </div>
      )}

      {!loading && selectedParentAgent && microAgents.length === 0 && miniAgents.length === 0 && (
        <div className="empty-state text-center py-5">
          <p className="text-muted">No resources found for this agent</p>
        </div>
      )}

      {!loading && selectedParentAgent && (
        <div className="resources-container">
          {renderInfrastructureSection()}
          {renderAgentsSection(microAgents, 'Micro-Agents')}
          {renderAgentsSection(miniAgents, 'Mini-Agents')}
          {renderApiStackSection()}
        </div>
      )}
    </Container>
  );
};

export default DeployAgent;